import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import apiService from '../services/api';
import { 
  BookOpen, 
  Search, 
  Plus, 
    Edit, 
  Trash2, 
  Star,
  Filter,
  X,
  Check,
  AlertCircle,
  Target,
  BarChart3,
  FileText,
  Clock,

  Info,
  GraduationCap,
  Save,
  Settings,
  Calendar
} from 'lucide-react';
import { SkillRelationType } from '../types';

interface Skill {
  id: number;
  name: string;
  description: string;
  category: SkillCategory;
  createdAt?: string;
  updatedAt?: string;
}

interface SkillCategory {
  id: number;
  name: string;
  description: string;
}

interface UserSkill {
  id: number;
  skill: Skill;
  relationType: 'TEACH' | 'LEARN';
  proficiencyLevel: number;
  proficiencyDescription: string;
  // Teaching-specific fields
  hourlyRate?: number; // Credits per session
  totalSessions?: number;
  // Common fields
  availability?: string;
}

interface AddSkillFormData {
  skillId: number;
  relationType: SkillRelationType;
  proficiencyLevel: number;
  proficiencyDescription: string;
  // Teaching-specific fields (only relevant for TEACH relation type)
  hourlyRate?: number; // Credits per session
  totalSessions?: number;
  // Common fields
  availability?: string;
  // New availability fields
  availableDays?: string[];
  availableTimeSlots?: string[];
  availabilityNotes?: string;
}

const Skills: React.FC = () => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [categories, setCategories] = useState<SkillCategory[]>([]);
  const [userSkills, setUserSkills] = useState<UserSkill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [showAddSkillModal, setShowAddSkillModal] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [editingSkill, setEditingSkill] = useState<UserSkill | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState<UserSkill | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<AddSkillFormData>();

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [skillsResponse, categoriesResponse, userSkillsResponse] = await Promise.all([
        apiService.getAllSkills(),
        apiService.getAllSkillCategories(),
        apiService.getUserSkills()
      ]);

      // Ensure we have valid data structures
      const skillsData = skillsResponse.data || [];
      const categoriesData = categoriesResponse.data || [];
      const userSkillsData = userSkillsResponse.data || [];

      // Filter out any skills without valid category data
      const validSkills = skillsData.filter(skill => skill && skill.name && skill.category);
      const validCategories = categoriesData.filter(category => category && category.name);
      const validUserSkills = userSkillsData.filter(userSkill => 
        userSkill && userSkill.skill && userSkill.skill.name
      );

      setSkills(validSkills);
      setCategories(validCategories);
      setUserSkills(validUserSkills);
    } catch (error: any) {
      toast.error('Failed to load skills data');
      console.error('Error fetching skills:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSkills = skills.filter(skill => {
    const matchesSearch = skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         skill.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || (skill.category && skill.category.id === selectedCategory);
    return matchesSearch && matchesCategory;
  });

  const handleAddSkill = async (data: AddSkillFormData) => {
    try {
      const response = await apiService.addUserSkill(data);
      setUserSkills(prev => [...prev, response.data]);
      setShowAddSkillModal(false);
      reset();
      toast.success('Skill added successfully!');
      
      // Add notification for new skill added
      addNotification({
        type: 'success',
        title: 'Skill Added Successfully',
        message: `Your skill "${response.data.skill.name}" has been added to your profile`,
        category: 'session'
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to add skill';
      toast.error(errorMessage);
      console.error('Add skill error:', error);
    }
  };

  const handleUpdateSkill = async (userSkillId: number, data: AddSkillFormData) => {
    try {
      const response = await apiService.updateUserSkill(userSkillId, data);
      setUserSkills(prev => prev.map(skill => 
        skill.id === userSkillId ? response.data : skill
      ));
      setEditingSkill(null);
      toast.success('Skill updated successfully!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update skill';
      toast.error(errorMessage);
      console.error('Update skill error:', error);
    }
  };

  const openDeleteModal = (userSkill: UserSkill) => {
    setSkillToDelete(userSkill);
    setShowDeleteModal(true);
  };

  const handleDeleteSkill = async () => {
    if (!skillToDelete) return;
    
    try {
      setIsSubmitting(true);
      await apiService.deleteUserSkill(skillToDelete.id);
      setUserSkills(prev => prev.filter(skill => skill.id !== skillToDelete.id));
      toast.success('Skill removed successfully!');
      setShowDeleteModal(false);
      setSkillToDelete(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to remove skill';
      toast.error(errorMessage);
      console.error('Delete skill error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSkillToDelete(null);
  };

  const openAddSkillModal = (skill: Skill) => {
    setSelectedSkill(skill);
    reset({
      skillId: skill.id,
      relationType: SkillRelationType.LEARN,
      proficiencyLevel: 1,
      proficiencyDescription: '',
      // Default values for new fields
      availability: '',
      // New availability fields
      availableDays: [],
      availableTimeSlots: [],
      availabilityNotes: ''
    });
    setShowAddSkillModal(true);
  };

  const openEditSkillModal = (userSkill: UserSkill) => {
    setEditingSkill(userSkill);
    reset({
      skillId: userSkill.skill.id,
      relationType: userSkill.relationType as SkillRelationType,
      proficiencyLevel: userSkill.proficiencyLevel,
      proficiencyDescription: userSkill.proficiencyDescription,
      // Include new fields
      hourlyRate: userSkill.hourlyRate,
      availability: userSkill.availability,
      totalSessions: userSkill.totalSessions,
      // New availability fields (with defaults for existing skills)
      availableDays: [],
      availableTimeSlots: [],
      availabilityNotes: ''
    });
  };

  const getTeachingSkills = () => userSkills.filter(skill => skill.relationType === SkillRelationType.TEACH);
  const getLearningSkills = () => userSkills.filter(skill => skill.relationType === SkillRelationType.LEARN);

  // Form progress calculation - Different for TEACH vs LEARN
  const getFormProgress = () => {
    const formData = watch();
    let completedFields = 0;
    
    // Common required fields
    if (formData.skillId) completedFields++;
    if (formData.relationType) completedFields++;
    if (formData.proficiencyLevel) completedFields++;
    if (formData.proficiencyDescription && formData.proficiencyDescription.trim()) completedFields++;
    
    if (formData.relationType === SkillRelationType.TEACH) {
      // Additional fields required for teaching
      if (formData.hourlyRate && formData.hourlyRate > 0) completedFields++;
      if (formData.availableDays && formData.availableDays.length > 0) completedFields++;
      return Math.min(1, completedFields / 6); // 6 total fields for teaching
    } else {
      // Only availability required for learning
      if (formData.availableDays && formData.availableDays.length > 0) completedFields++;
      return Math.min(1, completedFields / 5); // 5 total fields for learning
    }
  };

  const getFormProgressText = () => {
    const formData = watch();
    const progress = getFormProgress();
    
    if (progress === 0) return 'Start by selecting a skill';
    if (progress <= 0.2) return 'Great start! Keep going';
    if (progress <= 0.4) return 'Making good progress';
    if (progress <= 0.6) return 'Almost halfway there';
    if (progress <= 0.8) return 'Nearly complete!';
    if (progress < 1) {
      if (formData.relationType === SkillRelationType.TEACH) {
        return 'Just add your credits per session and availability';
      } else {
        return 'Just add your learning availability';
      }
    }
    return 'All set! Ready to submit';
  };

  const getProficiencyColor = (level: number) => {
    if (level >= 4) return 'text-green-600';
    if (level >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProficiencyText = (level: number) => {
    switch (level) {
      case 1: return 'Beginner';
      case 2: return 'Elementary';
      case 3: return 'Intermediate';
      case 4: return 'Advanced';
      case 5: return 'Expert';
      default: return 'Unknown';
    }
  };

  const onSubmit = async (data: AddSkillFormData) => {
    try {
      setIsSubmitting(true);
      
      // Create a clean data object with only the fields the backend expects
      const cleanData: any = {
        skillId: data.skillId,
        relationType: data.relationType,
        proficiencyLevel: data.proficiencyLevel,
        proficiencyDescription: data.proficiencyDescription,
        availability: data.availability
      };
      
      // Only include teaching-specific fields if user is teaching
      if (data.relationType === SkillRelationType.TEACH) {
        cleanData.hourlyRate = data.hourlyRate;
        cleanData.totalSessions = data.totalSessions;
      }
      
      console.log('Clean data for backend:', cleanData);
      
      // Process availability data
      let availabilityText = '';
      if (data.availableDays && data.availableDays.length > 0 || data.availableTimeSlots && data.availableTimeSlots.length > 0) {
        const availabilityParts = [];
        
        if (data.availableDays && data.availableDays.length > 0) {
          const days = data.availableDays.map(day => {
            if (day === 'flexible') return 'Flexible days';
            return day.charAt(0).toUpperCase() + day.slice(1);
          });
          availabilityParts.push(days.join(', '));
        }
        
        if (data.availableTimeSlots && data.availableTimeSlots.length > 0) {
          const timeSlots = data.availableTimeSlots.map(slot => {
            if (slot === 'flexible') return 'Flexible times';
            return slot.charAt(0).toUpperCase() + slot.slice(1);
          });
          availabilityParts.push(timeSlots.join(', '));
        }
        
        if (data.availabilityNotes) {
          availabilityParts.push(data.availabilityNotes);
        }
        
        availabilityText = availabilityParts.join(' | ');
      }
      
      // Update cleanData with processed availability
      cleanData.availability = availabilityText || data.availability || '';
      
      // Final debug log
      console.log('Final clean data being sent to API:', cleanData);
      
      if (editingSkill) {
        await apiService.updateUserSkill(editingSkill.id, cleanData);
        toast.success('Skill updated successfully!');
      } else {
        await apiService.addUserSkill(cleanData);
        toast.success('Skill added successfully!');
      }
      
      fetchData();
      reset();
      setShowAddSkillModal(false);
      setEditingSkill(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save skill');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4 pulse-glow"></div>
          <h2 className="text-xl font-bold gradient-text mb-2">Loading Skills...</h2>
          <p className="text-gray-600">Preparing your learning journey</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold gradient-text">Skills Management</h1>
            <p className="text-gray-600 mt-2 text-lg">Manage your teaching and learning skills</p>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-right">
              <p className="text-sm text-gray-500">Teaching Skills</p>
              <p className="text-2xl font-bold text-teal-600">{getTeachingSkills().length}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Learning Skills</p>
              <p className="text-2xl font-bold text-blue-600">{getLearningSkills().length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                placeholder="Search skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-300 focus:scale-[1.02]"
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : null)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-300"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {(searchQuery || selectedCategory) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory(null);
                }}
                className="p-2 text-gray-400 hover:text-blue-600 transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Available Skills */}
      <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Available Skills</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {skills.length > 0 && filteredSkills.map(skill => {
            const isAlreadyAdded = userSkills.some(us => us.skill.id === skill.id);
            return (
              <div key={skill.id} className="border border-blue-200 rounded-xl p-4 hover:shadow-lg hover:border-blue-300 transition-all duration-300 hover:scale-[1.02] group">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{skill.name}</h3>
                    <p className="text-sm text-gray-600 mt-2">{skill.description}</p>
                    <span className="inline-block bg-gradient-to-r from-blue-100 to-teal-100 text-blue-800 text-xs px-3 py-1 rounded-full mt-3 border border-blue-200">
                      {skill.category?.name || 'Uncategorized'}
                    </span>
                  </div>
                  {isAlreadyAdded ? (
                    <div className="flex items-center text-teal-600">
                      <div className="h-8 w-8 bg-teal-100 rounded-full flex items-center justify-center">
                        <Check className="h-4 w-4" />
                      </div>
                      <span className="text-sm ml-2 font-medium">Added</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => openAddSkillModal(skill)}
                      className="flex items-center text-blue-600 hover:text-blue-700 transition-colors duration-200 group-hover:scale-110"
                    >
                      <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <Plus className="h-4 w-4" />
                      </div>
                      <span className="text-sm ml-2 font-medium">Add</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {skills.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <div className="h-20 w-20 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <AlertCircle className="h-10 w-10 text-blue-600" />
            </div>
            <p className="text-gray-500 text-lg">No skills available</p>
          </div>
        )}
        {skills.length > 0 && filteredSkills.length === 0 && (
          <div className="text-center py-8">
            <div className="h-16 w-16 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <AlertCircle className="h-8 w-8 text-blue-600" />
            </div>
            <p className="text-gray-500 text-lg">No skills found matching your criteria</p>
          </div>
        )}
      </div>

      {/* User Skills */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Teaching Skills */}
        <div className="bg-white rounded-xl shadow-lg border border-teal-100 p-6 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Teaching Skills</h2>
            <span className="bg-gradient-to-r from-teal-100 to-blue-100 text-teal-800 text-sm px-3 py-1 rounded-full border border-teal-200 font-medium">
              {getTeachingSkills().length} skills
            </span>
          </div>
          <div className="space-y-3">
            {getTeachingSkills().length > 0 ? getTeachingSkills().map(userSkill => (
              <div key={userSkill.id} className="border border-teal-200 rounded-xl p-4 hover:shadow-md hover:border-teal-300 transition-all duration-300 bg-gradient-to-r from-teal-50 to-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{userSkill.skill.name}</h3>
                    <p className="text-sm text-gray-600">{userSkill.skill.category?.name || 'Uncategorized'}</p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${getProficiencyColor(userSkill.proficiencyLevel)}`}>
                        {getProficiencyText(userSkill.proficiencyLevel)}
                      </span>
                      <div className="flex ml-2">
                        {[1, 2, 3, 4, 5].map(level => (
                          <Star
                            key={level}
                            className={`h-4 w-4 ${
                              level <= userSkill.proficiencyLevel
                                ? 'text-yellow-500 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">{userSkill.proficiencyDescription}</p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => openEditSkillModal(userSkill)}
                      className="text-blue-600 hover:text-blue-700 p-2 hover:bg-blue-100 rounded-lg transition-all duration-200 hover:scale-110"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(userSkill)}
                      className="text-red-600 hover:text-red-700 p-2 hover:bg-red-100 rounded-lg transition-all duration-200 hover:scale-110"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-gray-500">
                <div className="h-16 w-16 bg-gradient-to-r from-teal-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <GraduationCap className="h-8 w-8 text-teal-600" />
                </div>
                <p className="text-lg">No teaching skills added yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Learning Skills */}
        <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Learning Skills</h2>
            <span className="bg-gradient-to-r from-blue-100 to-teal-100 text-blue-800 text-sm px-3 py-1 rounded-full border border-blue-200 font-medium">
              {getLearningSkills().length} skills
            </span>
          </div>
          <div className="space-y-3">
            {getLearningSkills().length > 0 ? getLearningSkills().map(userSkill => (
              <div key={userSkill.id} className="border border-blue-200 rounded-xl p-4 hover:shadow-md hover:border-blue-300 transition-all duration-300 bg-gradient-to-r from-blue-50 to-teal-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{userSkill.skill.name}</h3>
                    <p className="text-sm text-gray-600">{userSkill.skill.category?.name || 'Uncategorized'}</p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${getProficiencyColor(userSkill.proficiencyLevel)}`}>
                        {getProficiencyText(userSkill.proficiencyLevel)}
                      </span>
                      <div className="flex ml-2">
                        {[1, 2, 3, 4, 5].map(level => (
                          <Star
                            key={level}
                            className={`h-4 w-4 ${
                              level <= userSkill.proficiencyLevel
                                ? 'text-yellow-500 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">{userSkill.proficiencyDescription}</p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => openEditSkillModal(userSkill)}
                      className="text-blue-600 hover:text-blue-700 p-2 hover:bg-blue-100 rounded-lg transition-all duration-200 hover:scale-110"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(userSkill)}
                      className="text-red-600 hover:text-red-700 p-2 hover:bg-red-100 rounded-lg transition-all duration-200 hover:scale-110"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-gray-500">
                <div className="h-16 w-16 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <BookOpen className="h-8 w-8 text-blue-600" />
                </div>
                <p className="text-lg">No learning skills added yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

            {/* Enhanced Add/Edit Skill Modal */}
      {(showAddSkillModal || editingSkill) && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom-4 duration-300">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl z-10 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-full">
                    <BookOpen className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">
                      {editingSkill ? 'Edit Your Skill' : 'Add New Skill'}
                    </h2>
                    <p className="text-blue-100 text-sm">
                      {editingSkill ? 'Update your skill details' : 'Share your expertise or learning goals'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAddSkillModal(false);
                    setEditingSkill(null);
                    reset();
                  }}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            {/* Subtle separator line */}
            <div className="h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>

            {/* Form */}
            <form onSubmit={handleSubmit(editingSkill ? 
              (data) => handleUpdateSkill(editingSkill.id, data) : 
              handleAddSkill
            )} className="p-6 pt-8">
              
              {/* Progress Indicator */}
              <div className="mb-8">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Form Progress</span>
                  <span>{Math.min(100, Math.round((getFormProgress() * 100)))}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500 ease-out"
                    style={{ 
                      width: `${Math.min(100, getFormProgress() * 100)}%`,
                      maxWidth: '100%'
                    }}
                  ></div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {getFormProgressText()}
                </div>
              </div>
              
              <div className="space-y-6">
                {/* Skill Selection */}
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-xl border border-blue-100">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <BookOpen className="h-4 w-4 inline mr-2 text-blue-600" />
                    Selected Skill
                  </label>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={selectedSkill?.name || ''}
                        disabled
                        className="w-full px-3 py-2 border-0 bg-transparent text-lg font-semibold text-gray-900"
                      />
                      <p className="text-sm text-gray-600">{selectedSkill?.category?.name}</p>
                    </div>
                  </div>
                </div>

                {/* Relation Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    <Target className="h-4 w-4 inline mr-2 text-green-600" />
                    I want to...
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all ${
                      watch('relationType') === SkillRelationType.TEACH 
                        ? 'border-green-500 bg-green-50 shadow-lg' 
                        : 'border-gray-200 hover:border-green-300'
                    }`}>
                      <input
                        type="radio"
                        value={SkillRelationType.TEACH}
                        {...register('relationType', { required: true })}
                        className="sr-only"
                      />
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-full ${
                          watch('relationType') === SkillRelationType.TEACH 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          <GraduationCap className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Teach</div>
                          <div className="text-sm text-gray-600">Share your expertise</div>
                        </div>
                      </div>
                    </label>
                    
                    <label className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all ${
                      watch('relationType') === SkillRelationType.LEARN 
                        ? 'border-blue-500 bg-blue-50 shadow-lg' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}>
                      <input
                        type="radio"
                        value={SkillRelationType.LEARN}
                        {...register('relationType', { required: true })}
                        className="sr-only"
                      />
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-full ${
                          watch('relationType') === SkillRelationType.LEARN 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          <BookOpen className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Learn</div>
                          <div className="text-sm text-gray-600">Develop new skills</div>
                        </div>
                      </div>
                    </label>
                  </div>
                  {errors.relationType && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      Please select how you want to use this skill
                    </p>
                  )}
                </div>

                {/* Proficiency Level */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    <BarChart3 className="h-4 w-4 inline mr-2 text-purple-600" />
                    Proficiency Level
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      (Selected: {watch('proficiencyLevel') || 'None'})
                    </span>
                  </label>
                  
                  {/* Simple Radio Button Selection */}
                  <div className="space-y-3">
                    {[
                      { value: 1, label: 'Beginner', desc: 'Just starting out', color: 'bg-red-500' },
                      { value: 2, label: 'Elementary', desc: 'Basic understanding', color: 'bg-orange-500' },
                      { value: 3, label: 'Intermediate', desc: 'Some experience', color: 'bg-yellow-500' },
                      { value: 4, label: 'Advanced', desc: 'Considerable expertise', color: 'bg-green-500' },
                      { value: 5, label: 'Expert', desc: 'Master level', color: 'bg-blue-500' }
                    ].map((level) => (
                      <div key={level.value} className="flex items-center space-x-3">
                        <input
                          type="radio"
                          id={`proficiency-${level.value}`}
                          value={level.value}
                          {...register('proficiencyLevel', { 
                            required: 'Proficiency level is required'
                          })}
                          className="w-5 h-5 text-purple-600 border-gray-300 focus:ring-purple-500 cursor-pointer"
                        />
                        <label 
                          htmlFor={`proficiency-${level.value}`}
                          className="flex items-center space-x-3 flex-1 cursor-pointer p-3 rounded-lg border-2 transition-all duration-200 hover:bg-gray-50"
                        >
                          {/* Level Badge */}
                          <div className={`p-2 rounded-full ${level.color} text-white shadow-md`}>
                            <span className="font-bold text-sm">{level.value}</span>
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{level.label}</div>
                            <div className="text-sm text-gray-600">{level.desc}</div>
                            
                            {/* Star Rating */}
                            <div className="mt-1 flex items-center space-x-1">
                              {[1, 2, 3, 4, 5].map(star => (
                                <Star
                                  key={star}
                                  className={`h-3 w-3 ${
                                    star <= level.value
                                      ? 'text-yellow-400 fill-current' 
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                  
                  {errors.proficiencyLevel && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.proficiencyLevel.message}
                    </p>
                  )}
                </div>

                {/* Proficiency Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FileText className="h-4 w-4 inline mr-2 text-indigo-600" />
                    Tell us more about your experience
                  </label>
                  <textarea
                    {...register('proficiencyDescription')}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-none hover:border-indigo-300"
                    placeholder="Describe your approach, experience, teaching style, or learning goals..."
                  />
                </div>

                {/* Enhanced Post Information - Only show for TEACHING */}
                {watch('relationType') === SkillRelationType.TEACH && (
                  <div className="bg-gradient-to-r from-gray-50 to-indigo-50 p-4 rounded-xl border border-indigo-100">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                      <Settings className="h-4 w-4 mr-2 text-indigo-600" />
                      Teaching Details
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Credits per Session */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Star className="h-4 w-4 inline mr-2 text-green-600" />
                          Credits per Session
                        </label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 bg-gradient-to-r from-blue-500 to-purple-600 rounded flex items-center justify-center">
                            <span className="text-white text-xs font-bold">💎</span>
                          </div>
                          <input
                            type="number"
                            {...register('hourlyRate', { 
                              required: 'Credits per session is required for teaching',
                              min: { value: 1, message: 'Credits per session must be at least 1' },
                              max: { value: 1000, message: 'Credits per session cannot exceed 1000' }
                            })}
                            className="w-full pl-12 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all hover:border-green-300"
                            placeholder="25"
                            step="1"
                          />
                        </div>
                      </div>

                      {/* Total Sessions */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Calendar className="h-4 w-4 inline mr-2 text-blue-600" />
                          Teaching Sessions Completed
                        </label>
                        <input
                          type="number"
                          {...register('totalSessions', { min: 0 })}
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-blue-300"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Availability - Different labels for TEACH vs LEARN */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Clock className="h-4 w-4 inline mr-2 text-orange-600" />
                    {watch('relationType') === SkillRelationType.TEACH 
                      ? 'When are you available to teach?' 
                      : 'When are you available to learn?'}
                  </label>
                  
                  {/* Day Selection */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-3">Select available days:</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {[
                        { key: 'monday', label: 'Monday', icon: '1️⃣' },
                        { key: 'tuesday', label: 'Tuesday', icon: '2️⃣' },
                        { key: 'wednesday', label: 'Wednesday', icon: '3️⃣' },
                        { key: 'thursday', label: 'Thursday', icon: '4️⃣' },
                        { key: 'friday', label: 'Friday', icon: '5️⃣' },
                        { key: 'saturday', label: 'Saturday', icon: '6️⃣' },
                        { key: 'sunday', label: 'Sunday', icon: '7️⃣' },
                        { key: 'flexible', label: 'Flexible', icon: '🔄' }
                      ].map((day) => (
                        <label key={day.key} className="flex items-center space-x-2 cursor-pointer p-2 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all">
                          <input
                            type="checkbox"
                            value={day.key}
                            {...register('availableDays')}
                            className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                          />
                          <span className="text-sm font-medium text-gray-700">{day.icon} {day.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Time Slots */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-3">Select time slots:</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        { key: 'morning', label: 'Morning', time: '6 AM - 12 PM', icon: '🌅' },
                        { key: 'afternoon', label: 'Afternoon', time: '12 PM - 6 PM', icon: '☀️' },
                        { key: 'evening', label: 'Evening', time: '6 PM - 10 PM', icon: '🌆' },
                        { key: 'night', label: 'Night', time: '10 PM - 2 AM', icon: '🌙' },
                        { key: 'weekend', label: 'Weekends', time: 'All day', icon: '📅' },
                        { key: 'flexible', label: 'Flexible', time: 'Any time', icon: '🔄' }
                      ].map((slot) => (
                        <label key={slot.key} className="flex items-center space-x-2 cursor-pointer p-2 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all">
                          <input
                            type="checkbox"
                            value={slot.key}
                            {...register('availableTimeSlots')}
                            className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                          />
                          <div>
                            <div className="text-sm font-medium text-gray-700">{slot.icon} {slot.label}</div>
                            <div className="text-xs text-gray-500">{slot.time}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Custom Availability (Optional) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional notes (optional):
                    </label>
                    <textarea
                      {...register('availabilityNotes')}
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all resize-none hover:border-orange-300"
                      placeholder="e.g., Prefer video calls, Available for urgent requests, Timezone: EST..."
                    />
                  </div>
                </div>


              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddSkillModal(false);
                    setEditingSkill(null);
                    reset();
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                >
                  Cancel
                </button>
                                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl text-sm font-medium hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline mr-2"></div>
                        {editingSkill ? 'Updating...' : 'Adding...'}
                      </>
                    ) : editingSkill ? (
                      <>
                        <Save className="h-4 w-4 inline mr-2" />
                        Update Skill
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 inline mr-2" />
                        Add Skill
                      </>
                    )}
                  </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && skillToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 animate-in slide-in-from-bottom-4 duration-300">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-white bg-opacity-20 rounded-full">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Delete Skill</h2>
                  <p className="text-red-100 text-sm">This action cannot be undone</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Remove "{skillToDelete.skill.name}"?
                    </h3>
                    <p className="text-sm text-gray-600">
                      {skillToDelete.relationType === 'TEACH' ? 'Teaching' : 'Learning'} skill
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-gray-700">Skill Details:</span>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>• Category: {skillToDelete.skill.category?.name || 'Uncategorized'}</p>
                    <p>• Level: {getProficiencyText(skillToDelete.proficiencyLevel)}</p>
                    {skillToDelete.relationType === 'TEACH' && skillToDelete.hourlyRate && (
                      <p>• Rate: {skillToDelete.hourlyRate} credits/session</p>
                    )}
                    {skillToDelete.proficiencyDescription && (
                      <p>• Description: {skillToDelete.proficiencyDescription}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Warning Message */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800 mb-1">Warning</p>
                    <p className="text-sm text-red-700">
                      Deleting this skill will remove it from your profile and all associated data including availability, 
                      session history, and ratings will be permanently lost.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 p-6 pt-0 border-t border-gray-200">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={isSubmitting}
                className="px-6 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteSkill}
                disabled={isSubmitting}
                className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl text-sm font-medium hover:from-red-600 hover:to-red-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete Skill
                  </>
                  )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Skills; 